import { Component } from '@theme/component';

/**
 * @typedef {Object} TabAccordionRefs
 * @property {HTMLElement[]} tabHeaders
 * @property {HTMLElement[]} tabContents
 */

/**
 * Tab Accordion Component
 * 
 * Displays as tabs on desktop (≥750px) and accordion on mobile (<750px)
 * Includes full accessibility support with ARIA attributes and keyboard navigation
 * 
 * @extends {Component<TabAccordionRefs>}
 */
export class TabAccordion extends Component {
  requiredRefs = ['tabHeaders', 'tabContents'];

  connectedCallback() {
    super.connectedCallback();
    this.setupAccessibility();
    this.setupKeyboardNavigation();
  }

  setupAccessibility() {
    const tabHeaders = /** @type {HTMLElement[]} */ (this.refs.tabHeaders);
    const tabContents = /** @type {HTMLElement[]} */ (this.refs.tabContents);

    if (!Array.isArray(tabHeaders) || !Array.isArray(tabContents)) return;
    if (!tabHeaders.length || !tabContents.length) return;

    this.firstTab = tabHeaders[0];
    this.lastTab = tabHeaders[tabHeaders.length - 1];

    // Set up ARIA attributes
    tabHeaders.forEach((tab, index) => {
      const content = tabContents[index];
      if (!content) return;

      const tabId = `tab-${tab.dataset.tab}`;
      const panelId = `panel-${tab.dataset.tab}`;

      // Tab ARIA attributes
      tab.setAttribute('role', 'tab');
      tab.setAttribute('id', tabId);
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      tab.tabIndex = index === 0 ? 0 : -1;

      // Panel ARIA attributes
      content.setAttribute('role', 'tabpanel');
      content.setAttribute('id', panelId);
      content.setAttribute('aria-labelledby', tabId);
      content.setAttribute('tabindex', '0');
    });

    // Set first tab as active
    if (this.firstTab) {
      this.setSelectedTab(this.firstTab);
    }
  }

  setupKeyboardNavigation() {
    const tabHeaders = /** @type {HTMLElement[]} */ (this.refs.tabHeaders);

    if (!Array.isArray(tabHeaders)) return;

    tabHeaders.forEach(tab => {
      tab.addEventListener('keydown', this.onKeydown.bind(this));
    });
  }

  /**
   * Set the selected tab and update ARIA states
   * @param {HTMLElement} currentTab 
   */
  setSelectedTab(currentTab) {
    const tabHeaders = /** @type {HTMLElement[]} */ (this.refs.tabHeaders);
    const tabContents = /** @type {HTMLElement[]} */ (this.refs.tabContents);

    if (!Array.isArray(tabHeaders) || !Array.isArray(tabContents)) return;

    tabHeaders.forEach((tab, index) => {
      const content = tabContents[index];
      if (!content) return;

      if (currentTab === tab) {
        // Active tab
        tab.setAttribute('aria-selected', 'true');
        tab.tabIndex = 0;
        tab.classList.add('active');
        content.classList.add('active');
      } else {
        // Inactive tabs
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
        tab.classList.remove('active');
        content.classList.remove('active');
      }
    });
  }

  /**
   * Move focus to specified tab
   * @param {HTMLElement} tab 
   */
  moveFocusToTab(tab) {
    tab.focus();
  }

  /**
   * Move focus to previous tab (with wrapping)
   * @param {HTMLElement} currentTab 
   */
  moveFocusToPreviousTab(currentTab) {
    if (currentTab === this.firstTab && this.lastTab) {
      this.moveFocusToTab(this.lastTab);
    } else {
      const tabHeaders = /** @type {HTMLElement[]} */ (this.refs.tabHeaders);
      if (!Array.isArray(tabHeaders)) return;
      const index = tabHeaders.indexOf(currentTab);
      const prevTab = tabHeaders[index - 1];
      if (prevTab) this.moveFocusToTab(prevTab);
    }
  }

  /**
   * Move focus to next tab (with wrapping)
   * @param {HTMLElement} currentTab 
   */
  moveFocusToNextTab(currentTab) {
    if (currentTab === this.lastTab && this.firstTab) {
      this.moveFocusToTab(this.firstTab);
    } else {
      const tabHeaders = /** @type {HTMLElement[]} */ (this.refs.tabHeaders);
      if (!Array.isArray(tabHeaders)) return;
      const index = tabHeaders.indexOf(currentTab);
      const nextTab = tabHeaders[index + 1];
      if (nextTab) this.moveFocusToTab(nextTab);
    }
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event 
   */
  onKeydown(event) {
    const tab = /** @type {HTMLElement} */ (event.currentTarget);
    let flag = false;

    switch (event.key) {
      case 'ArrowLeft':
        this.moveFocusToPreviousTab(tab);
        flag = true;
        break;

      case 'ArrowRight':
        this.moveFocusToNextTab(tab);
        flag = true;
        break;

      case 'Home':
        if (this.firstTab) this.moveFocusToTab(this.firstTab);
        flag = true;
        break;

      case 'End':
        if (this.lastTab) this.moveFocusToTab(this.lastTab);
        flag = true;
        break;

      case 'Enter':
      case ' ':
        this.setSelectedTab(tab);
        flag = true;
        break;

      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  /**
   * Handle tab header clicks
   * @param {Event} event 
   */
  handleTabClick(event) {
    const activeTab = /** @type {HTMLElement} */ (event.target);
    this.setSelectedTab(activeTab);
  }
}

customElements.define('tab-accordion', TabAccordion);